import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import Skeleton from '@mui/material/Skeleton';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { fromAccounts } from '../../store';
import { AccountRole } from '../../models';

const useStyles = makeStyles(theme => ({
  boxRow: {
    flexDirection: 'row',
    minWidth: '22.5%',
  },
  boxFlex: {
    display: 'flex',
    borderBottom: '1px solid #EEF1FA',
    gap: theme.spacing(1, 1),
  },
  boxColumn: {
    display: 'column',
    marginTop: theme.spacing(1),
  },
  paperRoot: {
    minWidth: theme.spacing(30),
    maxWidth: theme.spacing(45),
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
  },
  accountName: {
    fontSize: theme.spacing(2.5),
    fontWeight: theme.typography.fontWeightMedium,
    color: '#233145',
    maxHeight: theme.spacing(7),
    marginBottom: theme.spacing(0.5),
    width: theme.spacing(29.5),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  accountRole: {
    fontSize: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: '#808B9A',
  },
  subTitle: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  subFont: {
    fontSize: theme.spacing(1.75),
    fontWeight: theme.typography.fontWeightMedium,
    color: '#808B9A',
  },
  skeletonNumber: {
    display: 'inline-block',
    marginLeft: theme.spacing(1.75),
    float: 'right',
  },
  skeletonText: {
    display: 'inline-block',
  },
}));

interface AccountInfoProps {
  id: number;
  role?: AccountRole;
}

const Bold = styled('span')(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  marginLeft: theme.spacing(3),
  float: 'right',
  color: '#233145',
}));

const Avatar = styled('img')(({ theme }) => ({
  width: theme.spacing(7.5),
  height: theme.spacing(7.5),
  marginBottom: theme.spacing(2),
  marginRight: theme.spacing(1),
  marginTop: 'auto',
  borderRadius: '50%',
}));

const AccountInfo = (props: AccountInfoProps) => {
  const { id, role } = props;
  const classes = useStyles();
  const account = useSelector(fromAccounts.selectAccountById(id));

  return (
    <Box className={classes.boxRow} justifyContent="space-between">
      <Paper elevation={1} className={classes.paperRoot}>
        <Box className={classes.boxFlex}>
          <Avatar src="https://katalon-testops.s3.amazonaws.com/image/icon/defaultAvatar.png" alt="avatar" />
          <Box className={classes.boxColumn}>
            {account?.name !== undefined
              ? (
                <Typography variant="subtitle2" title={account.name} className={classes.accountName}>
                  {account.name}
                </Typography>
              ) : (<Skeleton width={200} className={classes.skeletonText} />)}

            {role !== undefined
              ? (
                <Typography variant="subtitle2" className={classes.accountRole}>
                  <FormattedMessage id="admin.home.title.account" />
                  &nbsp;
                  {AccountRole.getRoleFullName(role)}
                </Typography>
              )
              : (<Skeleton width={200} className={classes.skeletonText} />)}
          </Box>
        </Box>

        <Box className={classes.boxColumn}>
          <Typography variant="subtitle2" className={clsx(classes.subTitle, classes.subFont)}>
            <FormattedMessage
              id="admin.home.account.number.members"
              values={{ count: account?.numberAccountUsers }}
            />
            {account?.numberAccountUsers !== undefined
              ? (
                <Bold>{account.numberAccountUsers}</Bold>
              ) : (
                <Skeleton width={30} className={classes.skeletonNumber} />
              )}
          </Typography>
          <Typography variant="subtitle2" className={classes.subFont}>
            <FormattedMessage
              id="admin.home.account.number.organizations"
              values={{ count: account?.numberOrganizations }}
            />
            {account?.numberOrganizations !== undefined
              ? (
                <Bold>{account.numberOrganizations}</Bold>
              ) : (
                <Skeleton width={30} className={classes.skeletonNumber} />
              )}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AccountInfo;
